import type { DestFill, FillMode, FillQuality, FillSource } from './api'

/*
 * Bitrate fill (docs/bitrate-fill.md §3, §4): texts and numbers shared by the
 * admin pages and the console. The numbers are facts, shown neutrally: a
 * problem of an output is only what state_reason / health say.
 */

export const FILL_MODES: FillMode[] = ['source', 'floor', 'none']
export const FILL_QUALITIES: FillQuality[] = ['auto', '720p', '1080p', '1440p']
/** Threshold classes of a profile, in table order (GET /api/admin/fill answers them too). */
export const FILL_CLASSES = ['720p30', '720p60', '1080p30', '1080p60', '1440p30', '1440p60']

/** Short names (admin tables, the Live card). */
export const FILL_MODE_SHORT: Record<FillMode, string> = {
  source: 'As encoder',
  floor: 'Fill to floor',
  none: 'No fill',
}

/** What each mode does, one line (admin settings). */
export const FILL_MODE_HELP: Record<FillMode, string> = {
  source: 'The platform gets the bitrate the encoder sent: its filler data is written back.',
  floor: "Filler up to the profile's threshold for the quality, plus the margin. Nothing is added above it.",
  none: 'Only the useful video and audio. Saves outgoing traffic.',
}

export const FILL_QUALITY_LABEL: Record<FillQuality, string> = { auto: 'Auto', '720p': '720p', '1080p': '1080p', '1440p': '1440p' }

export const FILL_SOURCE_LABEL: Record<FillSource, string> = {
  destination: 'own choice',
  profile: 'platform profile',
  platform: 'platform default',
}

/** The console's wording for a destination of this platform (docs/bitrate-fill.md §4, level 3). */
export function consoleModeLabel(mode: FillMode, platformLabel: string): string {
  if (mode === 'source') return 'As encoder'
  if (mode === 'none') return 'No fill'
  return platformLabel === 'YouTube' ? "Fill to YouTube's recommendation" : 'Fill to the recommended bitrate'
}

/** "Fill to floor · 1080p" */
export function fillText(f: { mode: FillMode; quality: FillQuality }): string {
  return f.mode === 'floor' ? `${FILL_MODE_SHORT.floor} · ${FILL_QUALITY_LABEL[f.quality]}` : FILL_MODE_SHORT[f.mode]
}

/** "Fill to floor · Auto (platform profile)" */
export function effectiveText(f: DestFill): string {
  const src = f.mode === 'floor' && f.quality_source !== f.mode_source ? `${FILL_SOURCE_LABEL[f.mode_source]}; quality: ${FILL_SOURCE_LABEL[f.quality_source]}` : FILL_SOURCE_LABEL[f.mode_source]
  return `${fillText(f)} (${src})`
}

/** Share of the filler in a bitrate, whole percent; undefined without a bitrate. */
export function fillShare(addedKbps: number | undefined, totalKbps: number): number | undefined {
  if (!totalKbps || addedKbps === undefined) return undefined
  return Math.round((Math.max(0, addedKbps) / totalKbps) * 100)
}

/** Megabits with one decimal, "13.5" */
export const mbit = (kbps: number) => (kbps / 1000).toFixed(1)
